// Pure decision for the global expired-session handler.
//
// Kept in its own dependency-free module (no fetch, no import.meta.env)
// so it can be unit-tested directly without dragging the large api.ts
// module — and its browser-only globals — into the test/typecheck
// program. api.ts wires this into request().

/**
 * True when a `status` on `path` should be treated as a global
 * session-expiry signal (an authenticated call rejected because the
 * session lapsed) rather than a locally-meaningful 401.
 *
 * Auth + session + invite endpoints are EXCLUDED: a 401 from
 * /api/session is the normal "not signed in" answer, a 401 from
 * /api/auth/sign-in is a bad-password attempt SignInPage already
 * surfaces, and invite flows are pre-auth. Treating any of those as an
 * expiry would loop the user back through sign-in.
 */
export function shouldSignalSessionExpiry(
  path: string,
  status: number,
): boolean {
  if (status !== 401) return false;
  if (
    path.startsWith("/api/session") ||
    path.startsWith("/api/auth/") ||
    path.startsWith("/api/invites")
  ) {
    return false;
  }
  return true;
}

// ─── The "who is signed in?" check, retried through a database blip ───
//
// 2026-09-30: the production database hit its CPU limit at 12:48 PM Arizona
// and was reset; for ~40 seconds even one-row lookups stalled. Every data
// route already answers that with a retryable 503, but the boot-time
// /api/session check failed with a 500, and SessionProvider treated ANY
// failure there as "signed out". So everyone who loaded or refreshed a page
// in that window was sent to the sign-in screen while their session was
// still perfectly valid.
//
// The rule now: only an ANSWER ("no user") signs someone out. A failure to
// get an answer (the network, a 5xx) is retried quietly for about a minute,
// long enough to ride out a database reset. A 4xx is an answer, not a blip,
// so it is not retried. This can only keep a signed-in person signed in; it
// never grants access, because every data route still verifies the session
// on its own.

/** Waits between attempts: ~59s in all, on top of the transport's own
 *  three quick retries of a `session_unavailable` 503. */
export const SESSION_CHECK_RETRY_DELAYS_MS: readonly number[] = [2000, 4000, 8000, 15000, 15000, 15000];

/** True when a failed session check is worth asking again: no HTTP answer at
 *  all (a network failure has no `status`), or a server-side 5xx. */
export function isTransientSessionCheckFailure(err: unknown): boolean {
  const status =
    typeof err === "object" && err !== null && "status" in err
      ? (err as { status: unknown }).status
      : undefined;
  if (typeof status !== "number") return true;
  return status >= 500;
}

export type SessionCheckOutcome<T> =
  | { kind: "ok"; value: T }
  | { kind: "failed"; error: unknown }
  // A newer check or a deliberate sign-out started while this one was
  // waiting; its result must not overwrite theirs.
  | { kind: "superseded" };

export async function checkSessionWithRetry<T>(
  check: () => Promise<T>,
  opts: {
    sleep: (ms: number) => Promise<void>;
    isSuperseded: () => boolean;
    delaysMs?: readonly number[];
  },
): Promise<SessionCheckOutcome<T>> {
  const delays = opts.delaysMs ?? SESSION_CHECK_RETRY_DELAYS_MS;
  for (let attempt = 0; ; attempt++) {
    try {
      const value = await check();
      return opts.isSuperseded() ? { kind: "superseded" } : { kind: "ok", value };
    } catch (error) {
      if (opts.isSuperseded()) return { kind: "superseded" };
      const delay = delays[attempt];
      if (delay === undefined || !isTransientSessionCheckFailure(error)) {
        return { kind: "failed", error };
      }
      await opts.sleep(delay);
      if (opts.isSuperseded()) return { kind: "superseded" };
    }
  }
}
